import React, { useEffect, useState } from "react";
import { UserPlus, Link2, Trash2, RotateCcw, Mail, Shield, UserX } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

const ROLE_LABELS = {
  admin: "Root",
  super_admin: "Root",
  consulente: "Consulente",
  company_admin: "Amministratore azienda",
  external_consultant: "Professionista delegato",
  studio_operator: "Collaboratore",
  company_responsible: "Responsabile azienda",
  employee: "Dipendente",
};
const STATUS_LABELS = { active: "Attivo", invited: "Invitato", suspended: "Sospeso", disabled: "Disabilitato" };
const STATUS_TONES = {
  active: "bg-emerald-100 text-emerald-800", invited: "bg-blue-100 text-blue-800",
  suspended: "bg-amber-100 text-amber-800", disabled: "bg-slate-200 text-slate-700",
};

/**
 * Sezione "Accessi aziendali" da innestare nella scheda Azienda.
 * Mostra admin/responsabili collegati; permette invito, link, unlink, reset, resend, status.
 */
export default function CompanyAccessSection({ companyId, currentUser }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const isSuper = currentUser?.role === "admin" || currentUser?.role === "super_admin";
  const canManage = isSuper || currentUser?.role === "client" || currentUser?.role === "company_admin";

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/access-management/companies/${companyId}/access`);
      setItems(data.items || []);
    } catch (e) {
      toast.error("Errore caricamento accessi", { description: e?.response?.data?.detail });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [companyId]);

  const doAction = async (action, u, args = {}) => {
    if (action === "unlink") {
      if (!window.confirm(`Scollegare ${u.email} da questa azienda? L'utente non verrà eliminato.`)) return;
      try {
        await api.delete(`/access-management/companies/${companyId}/access/${u.id}`);
        toast.success("Scollegato");
        load();
      } catch (e) { toast.error("Errore", { description: e?.response?.data?.detail }); }
    } else if (action === "reset") {
      try {
        const { data } = await api.post(`/access-management/users/${u.id}/send-reset`);
        if (data.reset_url) {
          navigator.clipboard.writeText(data.reset_url);
          toast.success("Link reset copiato", { description: `Scade tra ${data.expires_hours}h` });
        } else { toast.success("Reset inviato via email"); }
      } catch (e) { toast.error("Errore", { description: e?.response?.data?.detail }); }
    } else if (action === "resend") {
      try {
        const { data } = await api.post(`/access-management/users/${u.id}/resend-invite`);
        if (data.invite_url) {
          navigator.clipboard.writeText(data.invite_url);
          toast.success("Nuovo invito copiato", { description: `${data.invalidated_previous} precedenti invalidati` });
        }
      } catch (e) { toast.error("Errore", { description: e?.response?.data?.detail }); }
    } else if (action === "suspend" || action === "disable" || action === "activate") {
      const status = action === "suspend" ? "suspended" : action === "disable" ? "disabled" : "active";
      try {
        await api.post(`/access-management/users/${u.id}/status`, { status });
        toast.success("Stato aggiornato");
        load();
      } catch (e) { toast.error("Errore", { description: e?.response?.data?.detail }); }
    }
  };

  if (!companyId) return null;

  return (
    <div className="space-y-3 mt-4 p-4 border rounded-lg bg-slate-50" data-testid="company-access-section">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-slate-600" />
          <h3 className="font-semibold text-slate-900">Accessi aziendali</h3>
          <Badge variant="outline">{items.length}</Badge>
        </div>
        {canManage && (
          <div className="space-x-2">
            <Button size="sm" variant="outline" onClick={() => setLinkOpen(true)} data-testid="link-existing-btn">
              <Link2 className="w-3 h-3 mr-1" /> Collega esistente
            </Button>
            <Button size="sm" onClick={() => setInviteOpen(true)} data-testid="company-invite-btn">
              <UserPlus className="w-3 h-3 mr-1" /> Invita nuovo
            </Button>
          </div>
        )}
      </div>
      <div className="bg-white rounded border max-h-64 overflow-y-auto">
        {loading && <div className="p-4 text-center text-slate-500 text-sm">Caricamento…</div>}
        {!loading && items.length === 0 && (
          <div className="p-4 text-center text-slate-500 text-sm">Nessun accesso collegato</div>
        )}
        {!loading && items.map(u => (
          <div key={u.id} className="flex items-center justify-between p-2 border-b last:border-0" data-testid={`ca-row-${u.id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{u.name || u.email}</div>
              <div className="text-xs text-slate-500">{u.email}</div>
            </div>
            <Badge variant="outline" className="mx-2">{ROLE_LABELS[u.role_normalized] || u.role}</Badge>
            <span className={`px-2 py-0.5 text-xs rounded ${STATUS_TONES[u.account_status] || "bg-slate-100"}`}>
              {STATUS_LABELS[u.account_status] || u.account_status}
            </span>
            {canManage && (
              <div className="ml-2 space-x-1 flex">
                {u.account_status === "invited" && (
                  <Button size="sm" variant="ghost" onClick={() => doAction("resend", u)} title="Reinvia invito">
                    <Mail className="w-3 h-3" />
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => doAction("reset", u)} title="Reset password">
                  <RotateCcw className="w-3 h-3" />
                </Button>
                {u.account_status === "active" && (
                  <Button size="sm" variant="ghost" onClick={() => doAction("suspend", u)} title="Sospendi">
                    <UserX className="w-3 h-3" />
                  </Button>
                )}
                {u.account_status !== "active" && (
                  <Button size="sm" variant="ghost" onClick={() => doAction("activate", u)} title="Attiva">
                    <Shield className="w-3 h-3" />
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => doAction("unlink", u)} title="Scollega dall'azienda">
                  <Trash2 className="w-3 h-3 text-red-500" />
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
      <CompanyInviteDialog open={inviteOpen} onOpenChange={setInviteOpen} companyId={companyId} onCreated={load} isSuper={isSuper} />
      <CompanyLinkDialog open={linkOpen} onOpenChange={setLinkOpen} companyId={companyId} onCreated={load} />
    </div>
  );
}

function CompanyInviteDialog({ open, onOpenChange, companyId, onCreated, isSuper }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("company_admin");
  const submit = async () => {
    if (!email || !name) return toast.error("Nome ed email obbligatori");
    try {
      const { data } = await api.post("/access-management/invite",
                                       { email, name, role, company_id: companyId });
      if (data.invite_url) {
        navigator.clipboard.writeText(data.invite_url);
        toast.success("Invito creato", { description: "Link copiato negli appunti" });
      }
      onCreated?.(); onOpenChange(false); setEmail(""); setName("");
    } catch (e) { toast.error("Errore", { description: e?.response?.data?.detail }); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent formDialog data-testid="company-invite-dialog">
        <DialogHeader><DialogTitle>Invita nuovo utente aziendale</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><label className="text-sm">Nome</label><Input value={name} onChange={e => setName(e.target.value)} data-testid="ca-invite-name" /></div>
          <div><label className="text-sm">Email</label><Input type="email" value={email} onChange={e => setEmail(e.target.value)} data-testid="ca-invite-email" /></div>
          <div><label className="text-sm">Ruolo</label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="ca-invite-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="company_admin">Amministratore</SelectItem>
                <SelectItem value="company_responsible">Responsabile</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={submit} data-testid="ca-invite-submit">Invia invito</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompanyLinkDialog({ open, onOpenChange, companyId, onCreated }) {
  const [candidates, setCandidates] = useState([]);
  const [userId, setUserId] = useState("");
  const [role, setRole] = useState("company_admin");
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get(`/access-management/companies/${companyId}/candidate-users`);
        setCandidates(data.items || []);
      } catch (e) { /* silent */ }
    })();
  }, [open, companyId]);
  const submit = async () => {
    if (!userId) return toast.error("Scegli un account");
    try {
      await api.post(`/access-management/companies/${companyId}/access/link`,
                     { user_id: userId, role });
      toast.success("Account collegato");
      onCreated?.(); onOpenChange(false); setUserId("");
    } catch (e) { toast.error("Errore", { description: e?.response?.data?.detail }); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="company-link-dialog">
        <DialogHeader>
          <DialogTitle>Collega account gestionale esistente</DialogTitle>
          <DialogDescription>Scegli un account già presente e assegna il ruolo aziendale.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><label className="text-sm">Account</label>
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger data-testid="ca-link-user"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
              <SelectContent>
                {candidates.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name || c.email} — {c.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div><label className="text-sm">Ruolo</label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="company_admin">Amministratore</SelectItem>
                <SelectItem value="company_responsible">Responsabile</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={submit} data-testid="ca-link-submit">Collega</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
